/**
 * SectionCrossValidationSheet — Priority #6 surface.
 *
 * Lets a PM pick one of the workspace's CatalogProducts and ask
 * "does this product meet the RFP's stated requirements for THIS
 * section?". Backed by GET /api/v2/rfp/{id}/sections/{idx}/cross-validate.
 *
 * The matching service (app/services/cross_validation.py) returns three
 * severities:
 *
 *   match            ✅ requirement is satisfied (green)
 *   missing_cert     🔴 RFP demands a cert the product doesn't carry
 *   potential_gap    🟠 spec phrase in RFP, not found in product attrs
 *
 * We deliberately surface both directions: green ticks build confidence
 * for matches the PM might not notice manually, and the AMBER bucket
 * leans towards over-flagging because the worst failure mode is a
 * missed compliance line, not a false alarm.
 */
import React, { useCallback, useEffect, useState } from "react";
import {
  Shield, CheckCircle2, XCircle, AlertTriangle, Loader2, Boxes,
} from "lucide-react";
import { toast } from "sonner";

import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { v2Api, v2 } from "@/lib/v2Api";


const SEVERITY_META = {
  match:         { label: "Match",            Icon: CheckCircle2,
                   bg: "bg-emerald-100 text-emerald-700",
                   ring: "border-emerald-200" },
  missing_cert:  { label: "Missing cert",     Icon: XCircle,
                   bg: "bg-rose-100 text-rose-700",
                   ring: "border-rose-200" },
  potential_gap: { label: "Potential gap",    Icon: AlertTriangle,
                   bg: "bg-amber-100 text-amber-700",
                   ring: "border-amber-200" },
};


function SummaryBar({ summary }) {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap gap-2 text-xs">
      <Badge className={SEVERITY_META.match.bg}>
        ✅ {summary.match} matches
      </Badge>
      <Badge className={SEVERITY_META.missing_cert.bg}>
        🔴 {summary.missing_cert} missing certs
      </Badge>
      <Badge className={SEVERITY_META.potential_gap.bg}>
        🟠 {summary.potential_gap} potential gaps
      </Badge>
    </div>
  );
}


function DiscrepancyRow({ row }) {
  const meta = SEVERITY_META[row.severity] || SEVERITY_META.potential_gap;
  const Icon = meta.Icon;
  return (
    <li className={`border rounded-md p-3 bg-white ${meta.ring}`}>
      <div className="flex items-start gap-2">
        <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${
          row.severity === "match" ? "text-emerald-500"
          : row.severity === "missing_cert" ? "text-rose-500"
          : "text-amber-500"
        }`} />
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className={`${meta.bg} text-[10px]`}>{meta.label}</Badge>
            <span className="text-[11px] font-mono text-slate-500">{row.kind}</span>
          </div>
          <p className="text-sm text-slate-800">{row.detail}</p>
          {row.evidence && (
            <p className="text-[11px] text-slate-500">
              From RFP: <span className="font-mono bg-slate-50 px-1 rounded">{row.evidence}</span>
            </p>
          )}
          {row.suggestions?.length > 0 && (
            <ul className="text-[11px] text-slate-600 pl-3 list-disc space-y-0.5">
              {row.suggestions.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </div>
      </div>
    </li>
  );
}


export default function SectionCrossValidationSheet({
  open, onOpenChange, rfpId, sectionIndex, sectionTitle,
}) {
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productId, setProductId] = useState(null);
  const [report, setReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load workspace products lazily — only when the sheet opens.
  useEffect(() => {
    if (!open || products.length > 0 || productsLoading) return;
    let cancelled = false;
    setProductsLoading(true);
    v2.get("/catalog/products")
      .then((r) => { if (!cancelled) setProducts(Array.isArray(r.data) ? r.data : []); })
      .catch((err) => {
        if (cancelled) return;
        toast.error(
          err?.response?.data?.detail ?? err?.message ?? "Could not load products"
        );
      })
      .finally(() => { if (!cancelled) setProductsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const runReport = useCallback(async (pid) => {
    if (!pid || sectionIndex == null || !rfpId) return;
    setReportLoading(true); setError(null);
    try {
      const data = await v2Api.rfp.crossValidateSection(rfpId, sectionIndex, pid);
      setReport(data);
    } catch (err) {
      setReport(null);
      setError(err?.response?.data?.detail ?? err?.message ?? "Cross-validation failed");
    } finally {
      setReportLoading(false);
    }
  }, [rfpId, sectionIndex]);

  // Reset state when the sheet closes so re-open is fresh.
  useEffect(() => {
    if (!open) {
      setReport(null);
      setError(null);
      setProductId(null);
    }
  }, [open]);

  const onPickProduct = (pid) => {
    setProductId(pid);
    if (pid) runReport(pid);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="px-4 pt-4 pb-3 border-b">
          <SheetTitle className="text-base font-semibold flex items-center gap-2">
            <Shield className="w-4 h-4 text-blue-500" />
            Cross-validate — {sectionTitle || "section"}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-500">
            Pick a product; we check the RFP's stated certs + key specs
            against what the product actually carries.
          </SheetDescription>
        </SheetHeader>

        <div className="px-4 py-3 border-b">
          <label className="text-xs uppercase tracking-wider text-slate-500 font-medium">
            Product
          </label>
          <select
            value={productId || ""}
            onChange={(e) => onPickProduct(e.target.value || null)}
            disabled={productsLoading}
            className="mt-1 w-full px-3 py-2 border rounded-md bg-white text-sm"
          >
            <option value="">— pick a product —</option>
            {products.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
                {p.brand ? ` · ${p.brand}` : ""}
                {p.model_number ? ` · ${p.model_number}` : ""}
              </option>
            ))}
          </select>
          {productsLoading && (
            <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
              <Loader2 className="w-3 h-3 animate-spin" /> Loading products…
            </p>
          )}
          {!productsLoading && products.length === 0 && (
            <p className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
              <Boxes className="w-3 h-3" /> No catalog products yet — add one
              under Products before cross-validating.
            </p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
          {reportLoading && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <Loader2 className="w-3.5 h-3.5 animate-spin" /> Running check…
            </div>
          )}
          {error && (
            <div className="flex items-center gap-2 text-sm text-rose-700">
              <AlertTriangle className="w-4 h-4" /> {error}
            </div>
          )}
          {!reportLoading && !error && report && (
            <>
              <SummaryBar summary={report.summary} />
              {report.discrepancies.length === 0 ? (
                <p className="text-xs italic text-slate-400">
                  Nothing flagged — the section has no cert/spec mentions
                  the matcher recognises, or every requirement is covered.
                </p>
              ) : (
                <ul className="space-y-2.5">
                  {report.discrepancies.map((d, i) => (
                    <DiscrepancyRow key={i} row={d} />
                  ))}
                </ul>
              )}
            </>
          )}
          {!reportLoading && !error && !report && productId == null && (
            <p className="text-xs italic text-slate-400">
              Pick a product to run the check.
            </p>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
